import * as React from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import CircularProgress from '@mui/joy/CircularProgress'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { auditDayOf, parseShadowScore } from '@numeo/types-core'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { formatToUserLocalTime } from '@/utils/date-formatting'
import { apiErrorMessage } from '../api'
import { formatAuditDay } from '../format'
import { timeZoneName, VIEWER_TIME_ZONE } from '../viewerTimeZone'
import { SHADOW_DIFFERENCES_PAGE_SIZE, ShadowDifferencesList, shadowDifferencesApi } from './api'
import { useGoBack } from './useGoBack'

/**
 * The jobs of one day whose extraction differs from the RateConf the legacy chain saved: the list behind the audit's "Shadow fields differ"
 * card. A row opens the job, field by field, beside its document. Back returns to Pipeline Audit.
 */
export default function ShadowDifferencesPage() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const today = auditDayOf(new Date(), VIEWER_TIME_ZONE)
    const dayParam = searchParams.get('day')
    const day = dayParam && /^\d{4}-\d{2}-\d{2}$/.test(dayParam) && dayParam <= today ? dayParam : today
    const projectName = searchParams.get('projectName') ?? ''
    // The page is part of the address, so coming back from a job lands on the page it was opened from.
    const page = Math.max(1, Number(searchParams.get('page')) || 1)
    const setPage = (next: number) => {
        const params = new URLSearchParams(searchParams)
        if (next === 1) params.delete('page')
        else params.set('page', String(next))
        setSearchParams(params, { replace: true })
    }
    const auditParams = new URLSearchParams()
    if (dayParam) auditParams.set('day', day)
    if (projectName) auditParams.set('projectName', projectName)
    const goBack = useGoBack(`/document-pipeline/audit${auditParams.toString() ? `?${auditParams.toString()}` : ''}`)

    const [list, setList] = React.useState<ShadowDifferencesList | null>(null)
    const [error, setError] = React.useState<string | null>(null)

    React.useEffect(() => {
        let current = true
        setList(null)
        setError(null)
        shadowDifferencesApi
            .list({ day, timeZone: VIEWER_TIME_ZONE, projectName: projectName || undefined, page })
            .then((loaded) => current && setList(loaded))
            .catch((err) => current && setError(apiErrorMessage(err, 'Failed to load the jobs that differ from the legacy chain')))
        return () => {
            current = false
        }
    }, [day, projectName, page])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button size="sm" variant="outlined" color="neutral" startDecorator={<ArrowBackIcon />} onClick={goBack}>
                    Pipeline Audit
                </Button>
                <Box>
                    <Typography level="h2">Jobs that differ from the legacy chain</Typography>
                    <Typography level="body-sm">
                        Shadow jobs enqueued on {formatAuditDay(day)} ({timeZoneName()}, your time zone){projectName && <> for {projectName}</>} whose
                        extraction differs from the RateConf the legacy chain saved in at least one field. Open one to see what differs, next to the document.
                    </Typography>
                </Box>
            </Box>

            {error && (
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            )}
            {!list && !error && <CircularProgress size="sm" />}

            {list && (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow size="sm" sx={{ '--TableCell-headBackground': 'var(--joy-palette-background-level1)', minWidth: 640 }}>
                        <thead>
                            <tr>
                                <th style={{ width: 170 }}>Enqueued</th>
                                <th style={{ width: '22%' }}>Tenant</th>
                                <th>File</th>
                                <th style={{ width: 110 }}>Agreement</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.rows.length === 0 && (
                                <tr>
                                    <td colSpan={4}>
                                        <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                            No job differs from the legacy chain on this day.
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                            {list.rows.map((job) => {
                                const open = () => navigate(`/document-pipeline/audit/shadow-differences/${job.id}`)
                                const score = parseShadowScore(job.result_ref)
                                return (
                                    <tr
                                        key={job.id}
                                        tabIndex={0}
                                        role="link"
                                        onClick={open}
                                        onKeyDown={(e) => e.key === 'Enter' && open()}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>{formatToUserLocalTime(job.enqueued_at)}</td>
                                        <td style={{ wordBreak: 'break-word' }}>{job.project_name}</td>
                                        <td style={{ wordBreak: 'break-word' }}>
                                            {job.filename ?? '—'}
                                            {job.page_count ? (
                                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                    {job.page_count} page{job.page_count === 1 ? '' : 's'}
                                                </Typography>
                                            ) : null}
                                        </td>
                                        <td>{score === null ? '—' : score.toFixed(3)}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {list && list.totalPages > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button size="sm" variant="outlined" color="neutral" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {list.totalPages} · {list.total} jobs ({SHADOW_DIFFERENCES_PAGE_SIZE} per page)
                    </Typography>
                    <Button size="sm" variant="outlined" color="neutral" disabled={page >= list.totalPages} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </Box>
            )}
        </Box>
    )
}
